import { AnalyticCategories, AnalyticService } from '@application/analytic/analytic.service';
import { withTranslation } from '@application/i18n/with_translation';
import { parseDateOfBirth } from '@application/parsers';
import { validateNin } from '@application/validators';
import { Button, Flex, InputRadio, InputText, Text } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { Title } from '@component/title.component';
import { RepresentablePatientsPicker } from '@feature/representation/representable-patients-picker.component';
import type { RepresentablePatient } from '@feature/representation/use-respondent-representation';
import { useResolve } from '@visiba/instantiation';
import { NiN, NiNFormatter } from '@visiba/national-identity-number';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

interface Props {
	country: keyof NiN.Implementations;
	allowRepresentative: boolean;
	representablePatients: RepresentablePatient[];
	initialNin: string | null;
	initialDateOfBirth: string | null;
	onContinue: (nin: string | null, dateOfBirth: string | null) => void;
	onRepresent: (id: string) => void;
	disabled: boolean;
}

const DATE_OF_BIRTH_PLACEHOLDERS: Partial<Record<keyof NiN.Implementations, string>> = {
	GB: 'DD/MM/YYYY',
	DK: 'DD.MM.YYYY',
	NL: 'DD-MM-YYYY',
};

function isDateOfBirthInThePast(dateOfBirth: Date | null): dateOfBirth is Date {
	return dateOfBirth !== null && dayjs().isAfter(dateOfBirth);
}

export function ScreenWhoIsRespondent({
	country,
	allowRepresentative,
	representablePatients,
	initialNin,
	initialDateOfBirth,
	onContinue,
	onRepresent,
	disabled,
}: Props) {
	const analytic = useResolve(AnalyticService);
	const {
		control,
		register,
		watch,
		handleSubmit,
		setError,
		clearErrors,
		formState: { errors },
	} = useForm({
		defaultValues: {
			respondent: initialNin ? 'representative' : 'me',
			nin: initialNin ?? '',
			dateOfBirth: initialDateOfBirth ?? '',
		},
		mode: 'onBlur',
		shouldUnregister: true,
	});
	const respondent = watch('respondent');
	const requiresDateOfBirth = !['SE', 'NO', 'FI'].includes(country);

	useEffect(() => {
		analytic.trackPageView({ customTitle: 'Care navigation: Who is respondent' });
	}, [analytic]);

	return (
		<form
			noValidate
			onSubmit={handleSubmit(({ respondent, nin, dateOfBirth }) => {
				if (disabled) return;
				if (respondent === 'representative' && !allowRepresentative) {
					setError('root', {
						message: withTranslation('View.Booking.ScreenWhoIsRespondant.ValidationError.Required.RepresentablePatient'),
					});
					return;
				}
				analytic.trackEvent(AnalyticCategories.RedRobin, 'Care navigation: Select respondent', respondent);
				if (respondent === 'me') {
					onContinue(null, null);
					return;
				}
				const normalizedNin = NiN.normalize(country, nin.trim());
				const parsedDateOfBirth = requiresDateOfBirth
					? parseDateOfBirth(dateOfBirth.trim())
					: NiN.produce(country).getDateOfBirth(normalizedNin);
				if (!isDateOfBirthInThePast(parsedDateOfBirth)) return;
				onContinue(NiNFormatter.format(country, normalizedNin), dayjs(parsedDateOfBirth).format('YYYY-MM-DD'));
			})}
		>
			<Flex gap={tokens.spacing.x2}>
				<Title title={withTranslation('View.Booking.ScreenWhoIsRespondant.Title')} titleSize='small' />
				<Controller
					name='respondent'
					control={control}
					render={({ field }) => (
						<InputRadio.Root
							label={withTranslation('View.Booking.ScreenWhoIsRespondant.Input.Label.WhoIsRespondant')}
							variant='outlined'
							value={field.value}
							onChange={(value) => {
								clearErrors();
								field.onChange(value);
							}}
							disabled={disabled}
							required
						>
							<InputRadio.Item value='me' label={withTranslation('View.Booking.ScreenWhoIsRespondant.Option.Me')} />
							<InputRadio.Item
								value='representative'
								label={withTranslation('View.Booking.ScreenWhoIsRespondant.Option.Representative')}
							/>
						</InputRadio.Root>
					)}
				/>
				{respondent === 'representative' ? (
					<>
						<fieldset disabled={disabled} style={{ border: 0, padding: 0, margin: 0 }}>
							<RepresentablePatientsPicker
								representablePatients={representablePatients}
								onSelect={(person) => onRepresent(person.Id)}
								showOrDivider={allowRepresentative}
							/>
						</fieldset>
						{allowRepresentative ? (
							<>
								<InputText
									{...register('nin', {
										validate: {
											format: (value) => validateNin(country, value.trim()) || withTranslation('Form.ValidationError.Format.Nin'),
											dateOfBirth: (value) =>
												requiresDateOfBirth ||
												isDateOfBirthInThePast(NiN.produce(country).getDateOfBirth(NiN.normalize(country, value.trim()))) ||
												withTranslation('Form.ValidationError.Format.Nin'),
										},
									})}
									label={withTranslation('Form.Input.Label.Nin')}
									description={withTranslation('View.Booking.ScreenWhoIsRespondant.RepresentativeNin.Description')}
									placeholder={NiNFormatter.formatWithPlaceholder(country, '')[1] ?? ''}
									invalid={errors.nin != null}
									invalidText={errors.nin?.message}
									required
									disabled={disabled}
								/>
								{requiresDateOfBirth ? (
									<InputText
										{...register('dateOfBirth', {
											validate: (value) =>
												isDateOfBirthInThePast(parseDateOfBirth(value.trim())) ||
												withTranslation('Form.ValidationError.Required.DateOfBirth'),
										})}
										label={withTranslation('Form.Input.Label.DateOfBirth')}
										placeholder={DATE_OF_BIRTH_PLACEHOLDERS[country] ?? ''}
										invalid={errors.dateOfBirth != null}
										invalidText={errors.dateOfBirth?.message}
										required
										disabled={disabled}
									/>
								) : null}
							</>
						) : null}
					</>
				) : null}
				{errors.root ? (
					<Text variant='bodyRegular' role='alert'>
						{errors.root.message}
					</Text>
				) : null}
				<Button type='submit' loading={disabled}>
					{withTranslation('PW.Common.Next')}
				</Button>
			</Flex>
		</form>
	);
}
